/**
 * Lightweight drop-in replacement for @tanstack/react-query.
 *
 * Exports: QueryClient, QueryClientProvider, useQueryClient,
 *          useQuery, useMutation, useInfiniteQuery
 *
 * Covers the subset of react-query features actually used in the codebase:
 *   - queryKey/queryFn/enabled/staleTime/refetchInterval/retry/select/placeholderData
 *   - useMutation with onMutate/onSuccess/onError/onSettled
 *   - invalidateQueries (prefix matching), setQueryData, getQueryData,
 *     cancelQueries, resetQueries, refetchQueries, getQueryCache().subscribe
 *   - useInfiniteQuery with getNextPageParam/initialPageParam
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export type QueryKey = readonly unknown[];

type SubscriberReason = 'invalidate' | 'update';

interface CacheEntry {
  data: unknown;
  dataUpdatedAt: number;
  /** Callbacks to notify mounted useQuery hooks for this key */
  subscribers: Set<(reason: SubscriberReason) => void>;
  /** Active AbortController for the in-flight fetch (if any) */
  abortController?: AbortController;
  /** Shared in-flight query promise to dedupe concurrent fetches across hooks */
  inFlightPromise?: Promise<unknown>;
}

type CacheEvent = {
  type: 'updated' | 'removed';
  query: { queryKey: unknown[] };
};

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function serializeKey(key: QueryKey): string {
  return JSON.stringify(key);
}

function keyMatchesFilter(cacheKey: unknown[], filterKey: unknown[], exact: boolean): boolean {
  if (exact) return JSON.stringify(cacheKey) === JSON.stringify(filterKey);
  if (filterKey.length > cacheKey.length) return false;
  for (let i = 0; i < filterKey.length; i++) {
    if (JSON.stringify(filterKey[i]) !== JSON.stringify(cacheKey[i])) return false;
  }
  return true;
}

// ────────────────────────────────────────────────────────────
// QueryClient
// ────────────────────────────────────────────────────────────

interface QueryClientOptions {
  defaultOptions?: {
    queries?: {
      staleTime?: number;
      retry?: boolean | number;
      refetchOnWindowFocus?: boolean;
      refetchOnReconnect?: boolean;
    };
  };
}

export class QueryClient {
  private cache = new Map<string, CacheEntry>();
  private cacheSubscribers = new Set<(event: CacheEvent) => void>();
  readonly defaultOptions: QueryClientOptions['defaultOptions'];

  constructor(opts?: QueryClientOptions) {
    this.defaultOptions = opts?.defaultOptions;
  }

  // ── read / write ──

  getQueryData<T = unknown>(key: QueryKey): T | undefined {
    const entry = this.cache.get(serializeKey(key));
    return entry?.data as T | undefined;
  }

  setQueryData<T = unknown>(
    key: QueryKey,
    updater: T | ((old: T | undefined) => T | undefined),
  ): void {
    const sk = serializeKey(key);
    const entry = this.cache.get(sk);
    const oldData = entry?.data as T | undefined;
    const newData =
      typeof updater === 'function'
        ? (updater as (old: T | undefined) => T | undefined)(oldData)
        : updater;

    if (entry) {
      entry.data = newData;
      entry.dataUpdatedAt = Date.now();
      entry.subscribers.forEach((cb) => cb('update'));
    } else {
      this.cache.set(sk, {
        data: newData,
        dataUpdatedAt: Date.now(),
        subscribers: new Set(),
      });
    }
    this.notifyCacheSubscribers({ type: 'updated', query: { queryKey: key as unknown[] } });
  }

  // ── invalidation ──

  async invalidateQueries(filters?: { queryKey?: QueryKey; exact?: boolean }): Promise<void> {
    if (!filters?.queryKey) {
      // Invalidate everything
      this.cache.forEach((entry) => {
        entry.dataUpdatedAt = 0; // mark stale
        entry.subscribers.forEach((cb) => cb('invalidate'));
      });
      return;
    }
    const exact = filters.exact ?? false;
    this.cache.forEach((entry, sk) => {
      const cacheKey = JSON.parse(sk) as unknown[];
      if (keyMatchesFilter(cacheKey, filters.queryKey! as unknown[], exact)) {
        entry.dataUpdatedAt = 0; // mark stale
        entry.subscribers.forEach((cb) => cb('invalidate'));
      }
    });
  }

  async cancelQueries(filters: { queryKey: QueryKey }): Promise<void> {
    const exact = false;
    this.cache.forEach((entry, sk) => {
      const cacheKey = JSON.parse(sk) as unknown[];
      if (keyMatchesFilter(cacheKey, filters.queryKey as unknown[], exact)) {
        entry.abortController?.abort();
      }
    });
  }

  resetQueries(): void {
    this.cache.forEach((entry) => {
      entry.data = undefined;
      entry.dataUpdatedAt = 0;
      entry.subscribers.forEach((cb) => cb());
    });
    this.cache.clear();
  }

  async refetchQueries(filters?: { queryKey?: QueryKey; exact?: boolean }): Promise<void> {
    // Same as invalidate — triggers re-fetch in mounted hooks
    return this.invalidateQueries(filters);
  }

  // ── cache subscription (used by useUnreadChatCount) ──

  getQueryCache() {
    return {
      subscribe: (cb: (event: CacheEvent) => void) => {
        this.cacheSubscribers.add(cb);
        return () => {
          this.cacheSubscribers.delete(cb);
        };
      },
    };
  }

  // ── internal helpers ──

  _getOrCreateEntry(key: QueryKey): CacheEntry {
    const sk = serializeKey(key);
    let entry = this.cache.get(sk);
    if (!entry) {
      entry = { data: undefined, dataUpdatedAt: 0, subscribers: new Set() };
      this.cache.set(sk, entry);
    }
    return entry;
  }

  _subscribe(key: QueryKey, cb: (reason: SubscriberReason) => void): () => void {
    const entry = this._getOrCreateEntry(key);
    entry.subscribers.add(cb);
    return () => {
      entry.subscribers.delete(cb);
    };
  }

  _isFresh(key: QueryKey, staleTime: number): boolean {
    const entry = this.cache.get(serializeKey(key));
    if (!entry || entry.data === undefined) return false;
    return Date.now() - entry.dataUpdatedAt < staleTime;
  }

  _setAbortController(key: QueryKey, ac: AbortController): void {
    const entry = this._getOrCreateEntry(key);
    entry.abortController = ac;
  }

  _getInFlightPromise<T = unknown>(key: QueryKey): Promise<T> | undefined {
    const entry = this.cache.get(serializeKey(key));
    return entry?.inFlightPromise as Promise<T> | undefined;
  }

  _setInFlightPromise(key: QueryKey, promise: Promise<unknown>): void {
    const entry = this._getOrCreateEntry(key);
    entry.inFlightPromise = promise;
  }

  _clearInFlightPromise(key: QueryKey, promise: Promise<unknown>): void {
    const entry = this.cache.get(serializeKey(key));
    if (entry?.inFlightPromise === promise) {
      entry.inFlightPromise = undefined;
    }
  }

  private notifyCacheSubscribers(event: CacheEvent) {
    this.cacheSubscribers.forEach((cb) => {
      try {
        cb(event);
      } catch {
        // swallow subscriber errors
      }
    });
  }
}

// ────────────────────────────────────────────────────────────
// React Context
// ────────────────────────────────────────────────────────────

const QueryClientContext = createContext<QueryClient | null>(null);

export function QueryClientProvider({
  client,
  children,
}: {
  client: QueryClient;
  children: ReactNode;
}) {
  return <QueryClientContext.Provider value={client}>{children}</QueryClientContext.Provider>;
}

export function useQueryClient(): QueryClient {
  const client = useContext(QueryClientContext);
  if (!client) throw new Error('useQueryClient must be used within QueryClientProvider');
  return client;
}

// ────────────────────────────────────────────────────────────
// useQuery
// ────────────────────────────────────────────────────────────

interface UseQueryOptions<TData = unknown, TError = Error> {
  queryKey: QueryKey;
  queryFn: (context: { queryKey: QueryKey; signal?: AbortSignal }) => Promise<TData>;
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?:
    | number
    | false
    | ((query: { state: { data: TData | undefined; error: TError | null } }) => number | false);
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
  retry?: boolean | number | ((failureCount: number, error: TError) => boolean);
  select?: (data: TData) => any;
  placeholderData?: TData | ((previousData: TData | undefined) => TData | undefined);
}

interface UseQueryResult<TData = unknown, TError = Error> {
  data: TData | undefined;
  error: TError | null;
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  status: 'pending' | 'error' | 'success';
  refetch: () => Promise<void>;
}

export function useQuery<TData = unknown, TError = Error>(
  options: UseQueryOptions<TData, TError>,
): UseQueryResult<TData, TError> {
  const client = useQueryClient();
  const {
    queryKey,
    queryFn,
    enabled = true,
    staleTime: staleTimeOpt,
    refetchInterval = false,
    retry: retryOpt,
    select,
    placeholderData,
  } = options;

  const staleTime = staleTimeOpt ?? client.defaultOptions?.queries?.staleTime ?? 0;
  const retryConfig = retryOpt ?? client.defaultOptions?.queries?.retry ?? false;

  const serializedKey = serializeKey(queryKey);

  // State
  const [, forceRender] = useState(0);
  const stateRef = useRef<{
    data: TData | undefined;
    error: TError | null;
    isFetching: boolean;
    hasEverFetched: boolean;
  }>({
    data: client.getQueryData<TData>(queryKey),
    error: null,
    isFetching: false,
    hasEverFetched: false,
  });

  // Keep previous data for placeholderData support
  const prevDataRef = useRef<TData | undefined>(stateRef.current.data);

  const rerender = useCallback(() => forceRender((n) => n + 1), []);

  const fetchData = useCallback(
    async (isRefetch = false) => {
      if (stateRef.current.isFetching) return;

      // Collapse bursts of polling refetches across multiple mounted consumers.
      if (isRefetch && client._isFresh(queryKey, 1000)) {
        const cached = client.getQueryData<TData>(queryKey);
        if (cached !== undefined) {
          stateRef.current.data = cached;
          stateRef.current.hasEverFetched = true;
          stateRef.current.error = null;
          rerender();
          return;
        }
      }

      const inFlight = client._getInFlightPromise<TData>(queryKey);
      if (inFlight) {
        stateRef.current.isFetching = true;
        rerender();
        try {
          const data = await inFlight;
          stateRef.current.data = data;
          stateRef.current.error = null;
          stateRef.current.hasEverFetched = true;
          prevDataRef.current = data;
        } catch (err: any) {
          if (err?.name !== 'AbortError') {
            stateRef.current.error = err as TError;
            stateRef.current.hasEverFetched = true;
          }
        } finally {
          stateRef.current.isFetching = false;
          rerender();
        }
        return;
      }

      // Check cache freshness
      if (!isRefetch && client._isFresh(queryKey, staleTime)) {
        const cached = client.getQueryData<TData>(queryKey);
        if (cached !== undefined) {
          stateRef.current.data = cached;
          stateRef.current.hasEverFetched = true;
          stateRef.current.error = null;
          rerender();
          return;
        }
      }

      stateRef.current.isFetching = true;
      rerender();

      const fetchPromise = (async () => {
        const ac = new AbortController();
        client._setAbortController(queryKey, ac);

        let lastError: TError | null = null;
        const maxRetries =
          typeof retryConfig === 'number' ? retryConfig : retryConfig === true ? 3 : 0;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
          try {
            const data = await queryFn({ queryKey, signal: ac.signal });
            client.setQueryData(queryKey, data);
            return data;
          } catch (err: any) {
            if (err?.name === 'AbortError') {
              throw err;
            }
            lastError = err;

            // Check retry function
            if (typeof retryConfig === 'function') {
              if (!(retryConfig as (fc: number, e: TError) => boolean)(attempt, err)) {
                break;
              }
              continue;
            }

            if (attempt >= maxRetries) break;
          }
        }

        throw lastError;
      })();

      client._setInFlightPromise(queryKey, fetchPromise);

      try {
        const data = await fetchPromise;
        stateRef.current.data = data;
        stateRef.current.error = null;
        stateRef.current.hasEverFetched = true;
        prevDataRef.current = data;
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          stateRef.current.error = err as TError;
          stateRef.current.hasEverFetched = true;
        }
      } finally {
        client._clearInFlightPromise(queryKey, fetchPromise);
        stateRef.current.isFetching = false;
        rerender();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serializedKey, enabled, staleTime],
  );

  // Subscribe to cache changes
  useEffect(() => {
    if (!enabled) return;
    const unsub = client._subscribe(queryKey, (reason) => {
      if (reason === 'invalidate') {
        // Cache was invalidated — refetch
        fetchData(true);
      } else if (!stateRef.current.isFetching) {
        // Another hook updated the cache — sync local state without refetching.
        // Skip when WE are the fetcher (setQueryData fires before our local state updates).
        const cached = client.getQueryData<TData>(queryKey);
        if (cached !== undefined && cached !== stateRef.current.data) {
          stateRef.current.data = cached;
          stateRef.current.hasEverFetched = true;
          stateRef.current.error = null;
          rerender();
        }
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled]);

  // Initial fetch
  useEffect(() => {
    if (!enabled) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled]);

  // Polling
  useEffect(() => {
    if (!enabled) return;

    type PollListener = {
      run: () => void | Promise<void>;
      getInterval: () => number | false | undefined;
    };
    type PollManager = {
      timeoutId: ReturnType<typeof setTimeout> | null;
      listeners: Set<PollListener>;
    };

    const getInterval = () => {
      if (typeof refetchInterval === 'function') {
        return refetchInterval({
          state: {
            data: stateRef.current.data,
            error: stateRef.current.error,
          },
        });
      }
      return refetchInterval;
    };

    const isValidInterval = (v: number | false | undefined): v is number =>
      !!v && Number.isFinite(v) && v > 0;

    const mapKey = '__queryPollingManagers';
    const globalMap: Map<string, PollManager> =
      (globalThis as any)[mapKey] ?? ((globalThis as any)[mapKey] = new Map<string, PollManager>());
    const managerKey = serializedKey;

    let manager = globalMap.get(managerKey);
    if (!manager) {
      manager = { timeoutId: null, listeners: new Set() };
      globalMap.set(managerKey, manager);
    }

    const listener: PollListener = {
      run: () => fetchData(true),
      getInterval,
    };
    manager.listeners.add(listener);

    const schedule = () => {
      if (!manager) return;
      if (manager.timeoutId) return;

      const intervals = Array.from(manager.listeners)
        .map((l) => l.getInterval())
        .filter(isValidInterval);
      if (!intervals.length) return;

      const nextInterval = Math.min(...intervals);
      manager.timeoutId = setTimeout(async () => {
        if (!manager) return;
        manager.timeoutId = null;
        await Promise.allSettled(
          Array.from(manager.listeners).map((l) => {
            try {
              return l.run();
            } catch {
              return undefined;
            }
          }),
        );
        if (globalMap.get(managerKey) === manager) {
          schedule();
        }
      }, nextInterval);
    };

    schedule();

    return () => {
      if (!manager) return;
      manager.listeners.delete(listener);
      if (manager.listeners.size === 0) {
        if (manager.timeoutId) clearTimeout(manager.timeoutId);
        manager.timeoutId = null;
        globalMap.delete(managerKey);
      } else if (!manager.timeoutId) {
        schedule();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled, refetchInterval]);

  // Cache sync is handled by the subscriber above — no separate effect needed.

  const state = stateRef.current;
  let data = state.data;

  // Apply select transform
  if (select && data !== undefined) {
    try {
      data = select(data as TData);
    } catch {
      // select failed, return raw data
    }
  }

  // Apply placeholderData when we have no data
  if (data === undefined && placeholderData !== undefined) {
    if (typeof placeholderData === 'function') {
      data = (placeholderData as (prev: TData | undefined) => TData | undefined)(
        prevDataRef.current,
      );
    } else {
      data = placeholderData;
    }
  }

  const hasData = data !== undefined;
  const isLoading = !hasData && state.isFetching;
  const isError = state.error !== null && !hasData;
  const isSuccess = hasData && !state.error;

  return {
    data: data as TData | undefined,
    error: state.error,
    isLoading,
    isPending: isLoading,
    isFetching: state.isFetching,
    isRefetching: state.isFetching && hasData,
    isError,
    isSuccess,
    status: isError ? 'error' : isSuccess ? 'success' : 'pending',
    refetch: () => fetchData(true),
  };
}

// ────────────────────────────────────────────────────────────
// useMutation
// ────────────────────────────────────────────────────────────

interface UseMutationOptions<TData, TError, TVariables, TContext = unknown> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onMutate?: (variables: TVariables) => Promise<TContext | undefined> | TContext | undefined;
  onSuccess?: (
    data: TData,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<void>;
  onError?: (
    error: TError,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<void>;
  onSettled?: (
    data: TData | undefined,
    error: TError | null,
    variables: TVariables,
    context: TContext | undefined,
  ) => void | Promise<void>;
}

interface UseMutationResult<TData, TError, TVariables> {
  mutate: (
    variables: TVariables,
    callbacks?: {
      onSuccess?: (data: TData) => void;
      onError?: (error: TError) => void;
    },
  ) => void;
  mutateAsync: (
    variables: TVariables,
    callbacks?: {
      onSuccess?: (data: TData) => void;
      onError?: (error: TError) => void;
    },
  ) => Promise<TData>;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: TError | null;
  data: TData | undefined;
  reset: () => void;
}

export function useMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables> {
  const [state, setState] = useState<{
    isPending: boolean;
    isError: boolean;
    isSuccess: boolean;
    error: TError | null;
    data: TData | undefined;
  }>({
    isPending: false,
    isError: false,
    isSuccess: false,
    error: null,
    data: undefined,
  });

  // Keep latest options in a ref so closures always see current callbacks
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutateAsync = useCallback(
    async (
      variables: TVariables,
      callbacks?: {
        onSuccess?: (data: TData) => void;
        onError?: (error: TError) => void;
      },
    ): Promise<TData> => {
      const opts = optionsRef.current;
      setState({ isPending: true, isError: false, isSuccess: false, error: null, data: undefined });

      let context: TContext | undefined;
      try {
        context = (await opts.onMutate?.(variables)) as TContext | undefined;
        const data = await opts.mutationFn(variables);
        setState({ isPending: false, isError: false, isSuccess: true, error: null, data });
        await opts.onSuccess?.(data, variables, context);
        callbacks?.onSuccess?.(data);
        await opts.onSettled?.(data, null, variables, context);
        return data;
      } catch (error: any) {
        setState({ isPending: false, isError: true, isSuccess: false, error, data: undefined });
        await opts.onError?.(error, variables, context);
        callbacks?.onError?.(error);
        await opts.onSettled?.(undefined, error, variables, context);
        throw error;
      }
    },
    [],
  );

  const mutate = useCallback(
    (
      variables: TVariables,
      callbacks?: {
        onSuccess?: (data: TData) => void;
        onError?: (error: TError) => void;
      },
    ) => {
      mutateAsync(variables, callbacks).catch(() => {
        // swallow — error is captured in state
      });
    },
    [mutateAsync],
  );

  const reset = useCallback(() => {
    setState({ isPending: false, isError: false, isSuccess: false, error: null, data: undefined });
  }, []);

  return {
    mutate,
    mutateAsync,
    isPending: state.isPending,
    isError: state.isError,
    isSuccess: state.isSuccess,
    error: state.error,
    data: state.data,
    reset,
  };
}

// ────────────────────────────────────────────────────────────
// useInfiniteQuery
// ────────────────────────────────────────────────────────────

interface UseInfiniteQueryOptions<TData = unknown> {
  queryKey: QueryKey;
  queryFn: (context: { queryKey: QueryKey; pageParam: any }) => Promise<TData>;
  getNextPageParam: (lastPage: TData, allPages: TData[]) => any | undefined;
  initialPageParam: any;
  enabled?: boolean;
  staleTime?: number;
}

interface UseInfiniteQueryResult<TData = unknown> {
  data: { pages: TData[]; pageParams: any[] } | undefined;
  error: Error | null;
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  isSuccess: boolean;
  status: 'pending' | 'error' | 'success';
  fetchNextPage: () => Promise<void>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  refetch: () => Promise<void>;
}

export function useInfiniteQuery<TData = unknown>(
  options: UseInfiniteQueryOptions<TData>,
): UseInfiniteQueryResult<TData> {
  const client = useQueryClient();
  const {
    queryKey,
    queryFn,
    getNextPageParam,
    initialPageParam,
    enabled = true,
    staleTime: staleTimeOpt,
  } = options;

  const staleTime = staleTimeOpt ?? client.defaultOptions?.queries?.staleTime ?? 0;
  const serializedKey = serializeKey(queryKey);

  const [state, setState] = useState<{
    pages: TData[];
    pageParams: any[];
    error: Error | null;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    hasEverFetched: boolean;
  }>({
    pages: [],
    pageParams: [],
    error: null,
    isFetching: false,
    isFetchingNextPage: false,
    hasNextPage: false,
    hasEverFetched: false,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const fetchFirstPage = useCallback(async () => {
    if (!enabled) return;
    if (stateRef.current.isFetching) return;

    setState((s) => ({ ...s, isFetching: true }));
    try {
      const data = await queryFn({ queryKey, pageParam: initialPageParam });
      const nextPageParam = getNextPageParam(data, [data]);
      setState({
        pages: [data],
        pageParams: [initialPageParam],
        error: null,
        isFetching: false,
        isFetchingNextPage: false,
        hasNextPage: nextPageParam !== undefined,
        hasEverFetched: true,
      });
    } catch (err: any) {
      setState((s) => ({ ...s, error: err, isFetching: false, hasEverFetched: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled]);

  const fetchNextPage = useCallback(async () => {
    const s = stateRef.current;
    if (!s.hasNextPage || s.isFetchingNextPage) return;

    const lastPage = s.pages[s.pages.length - 1];
    const nextParam = getNextPageParam(lastPage, s.pages);
    if (nextParam === undefined) return;

    setState((prev) => ({ ...prev, isFetchingNextPage: true, isFetching: true }));
    try {
      const data = await queryFn({ queryKey, pageParam: nextParam });
      setState((prev) => {
        const newPages = [...prev.pages, data];
        const newParams = [...prev.pageParams, nextParam];
        const nextNext = getNextPageParam(data, newPages);
        return {
          ...prev,
          pages: newPages,
          pageParams: newParams,
          hasNextPage: nextNext !== undefined,
          isFetchingNextPage: false,
          isFetching: false,
          error: null,
        };
      });
    } catch (err: any) {
      setState((prev) => ({ ...prev, error: err, isFetchingNextPage: false, isFetching: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey]);

  // Initial fetch
  useEffect(() => {
    if (!enabled) return;
    fetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled]);

  // Subscribe to invalidations (infinite queries only refetch on invalidate)
  useEffect(() => {
    if (!enabled) return;
    return client._subscribe(queryKey, (reason) => {
      if (reason === 'invalidate') fetchFirstPage();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedKey, enabled]);

  const hasData = state.pages.length > 0;
  const isLoading = !hasData && state.isFetching;
  const isError = !!state.error && !hasData;
  const isSuccess = hasData;

  return {
    data: hasData ? { pages: state.pages, pageParams: state.pageParams } : undefined,
    error: state.error,
    isLoading,
    isPending: isLoading,
    isFetching: state.isFetching,
    isError,
    isSuccess,
    status: isError ? 'error' : isSuccess ? 'success' : 'pending',
    fetchNextPage,
    hasNextPage: state.hasNextPage,
    isFetchingNextPage: state.isFetchingNextPage,
    refetch: fetchFirstPage,
  };
}
